import { MoveRisk, MoveGroup, DriverWithMoves } from './types';

const LOCATION_ABBREVIATIONS: Record<string, string> = {
  'International': 'Int',
  'Maintenance': 'Maint',
  'Distribution': 'Dist',
  'Container': 'Cont',
  'Transportation': 'Trans',
  'Logistics': 'Log',
  'Manufacturing': 'Mfg',
  'Warehouse': 'WH',
  'Terminal': 'Term',
  'Incorporated': 'Inc',
  'Corporation': 'Corp',
  'Company': 'Co',
  'Limited': 'Ltd',
  'Services': 'Svc',
  'Industries': 'Ind',
};

/**
 * Abbreviate common words in location names to save space, uppercase, and truncate
 */
export function abbreviateLocationName(name: string, maxLength: number = 25): string {
  if (!name) return name;
  let result = name;
  for (const [full, abbrev] of Object.entries(LOCATION_ABBREVIATIONS)) {
    // Case-insensitive replace, preserve word boundaries
    const regex = new RegExp(`\\b${full}\\b`, 'gi');
    result = result.replace(regex, abbrev);
  }
  result = result.toUpperCase();
  if (result.length > maxLength) {
    result = result.slice(0, maxLength - 3) + '...';
  }
  return result;
}

export function formatRequestAge(isoString: string | null | undefined): string {
  if (!isoString) return '';
  const t = new Date(isoString).getTime();
  if (Number.isNaN(t)) return '';
  const diffMs = Date.now() - t;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hrs < 24) return remMins ? `${hrs}h ${remMins}m ago` : `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function formatMinutes(minutes: number | null): string {
  if (minutes === null) return '-';
  const isNegative = minutes < 0;
  const absMinutes = Math.abs(minutes);
  const prefix = isNegative ? '-' : '';

  if (absMinutes >= 1440) {
    const days = Math.floor(absMinutes / 1440);
    const hours = Math.floor((absMinutes % 1440) / 60);
    const mins = absMinutes % 60;
    return `${prefix}${days}d ${hours}h ${mins}min`;
  }
  if (absMinutes >= 60) {
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    return `${prefix}${hours}h ${mins}min`;
  }
  return `${prefix}${absMinutes}min`;
}

export function formatMinutesCompact(minutes: number | null): string {
  if (minutes === null) return '-';
  const isNegative = minutes < 0;
  const absMinutes = Math.abs(minutes);
  const prefix = isNegative ? '-' : '';

  if (absMinutes >= 1440) {
    const days = Math.floor(absMinutes / 1440);
    const hours = Math.floor((absMinutes % 1440) / 60);
    const mins = absMinutes % 60;
    return `${prefix}${days}d${hours}h${mins}m`;
  }
  if (absMinutes >= 60) {
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    return `${prefix}${hours}h${mins}m`;
  }
  return `${prefix}${absMinutes}m`;
}

/**
 * Whether a driver (via its moves) matches the selected container sizes.
 * Empty selection means "no filter" and matches everything. A driver matches
 * if ANY of its moves has a size in the selection (OR across moves and
 * across the selection). Blank/whitespace sizes never match a real selection.
 */
export function driverMatchesSizes(moves: MoveRisk[], selectedSizes: string[]): boolean {
  if (selectedSizes.length === 0) return true;
  const wanted = new Set(selectedSizes.map(s => s.trim()));
  return moves.some(m => {
    const size = (m.size || '').trim();
    return size !== '' && wanted.has(size);
  });
}

/**
 * Whether a driver (via its moves) matches the selected chassis types.
 * Empty selection means "no filter" and matches everything. A driver matches
 * if ANY of its moves has a chassistype in the selection. Comparison is
 * case-insensitive; null/blank chassistypes never match a real selection.
 */
export function driverMatchesChassisTypes(moves: MoveRisk[], selectedChassisTypes: string[]): boolean {
  if (selectedChassisTypes.length === 0) return true;
  const wanted = new Set(selectedChassisTypes.map(s => s.toUpperCase().trim()));
  return moves.some(m => {
    const chassis = (m.chassistype || '').toUpperCase().trim();
    return chassis !== '' && wanted.has(chassis);
  });
}

/**
 * Canonical duty-status buckets. Raw values come from the Samsara HOS cache
 * (offDuty, sleeperBed, driving, onDuty, yardMove, personalConveyance), but
 * spaced/upper-cased variants ("OFF DUTY") also occur, so classification is by
 * substring. The CompactHosWidget badge and the Duty Status filter both key
 * off this so what the lane shows always agrees with what the filter matches.
 */
export type DutyStatusKey = 'off_duty' | 'sleeper' | 'driving' | 'on_duty' | 'yard_move' | 'personal';

/** Filter value for drivers with no HOS record (null/blank duty status). */
export const NO_HOS_DUTY_STATUS = 'no_hos';

export function normalizeDutyStatus(status: string | null | undefined): DutyStatusKey | null {
  if (!status || status.trim() === '') return null;
  const s = status.toLowerCase();
  // "off duty" must be tested before "on duty": both contain "duty", and
  // "on" is a substring of many words (incl. "personalConveyance").
  if (s.includes('off') && s.includes('duty')) return 'off_duty';
  if (s.includes('sleeper')) return 'sleeper';
  if (s.includes('driving')) return 'driving';
  if (s.includes('on') && s.includes('duty')) return 'on_duty';
  if (s.includes('yard')) return 'yard_move';
  if (s.includes('personal')) return 'personal';
  return null;
}

/**
 * Whether a driver matches the selected duty statuses. Empty selection means
 * "no filter" and matches everything. Selection values are DutyStatusKey
 * strings plus NO_HOS_DUTY_STATUS, which matches only a missing/blank status.
 * An unrecognized raw status matches nothing.
 */
export function driverMatchesDutyStatuses(
  currentDutyStatus: string | null | undefined,
  selectedDutyStatuses: string[],
): boolean {
  if (selectedDutyStatuses.length === 0) return true;
  const key = normalizeDutyStatus(currentDutyStatus);
  if (key) return selectedDutyStatuses.includes(key);
  const isMissing = !currentDutyStatus || currentDutyStatus.trim() === '';
  return isMissing && selectedDutyStatuses.includes(NO_HOS_DUTY_STATUS);
}

/**
 * Group consecutive legs on the same PRO number.
 * Legs are grouped when they have consecutive leg numbers and the previous leg is not a drop.
 */
export function groupConsecutiveLegs(moves: MoveRisk[]): MoveGroup[] {
  if (moves.length === 0) return [];

  const groups: MoveGroup[] = [];
  let currentGroup: MoveRisk[] = [];
  let currentPro: number | null = null;

  for (const move of moves) {
    if (currentPro === null) {
      currentPro = move.pronumber;
      currentGroup = [move];
    } else if (move.pronumber === currentPro) {
      const prevMove = currentGroup[currentGroup.length - 1];
      const isConsecutive = move.legnum === prevMove.legnum + 1;
      const prevWasDrop = prevMove.legdrop === 'Y';

      if (isConsecutive && !prevWasDrop) {
        currentGroup.push(move);
      } else {
        groups.push({
          id: currentGroup.length > 1
            ? `group_${currentGroup[0].pronumber}_${currentGroup[0].legnum}`
            : `${currentGroup[0].pronumber}-${currentGroup[0].legnum}`,
          moves: currentGroup,
          isGroup: currentGroup.length > 1,
        });
        currentGroup = [move];
      }
    } else {
      groups.push({
        id: currentGroup.length > 1
          ? `group_${currentGroup[0].pronumber}_${currentGroup[0].legnum}`
          : `${currentGroup[0].pronumber}-${currentGroup[0].legnum}`,
        moves: currentGroup,
        isGroup: currentGroup.length > 1,
      });
      currentPro = move.pronumber;
      currentGroup = [move];
    }
  }

  if (currentGroup.length > 0) {
    groups.push({
      id: currentGroup.length > 1
        ? `group_${currentGroup[0].pronumber}_${currentGroup[0].legnum}`
        : `${currentGroup[0].pronumber}-${currentGroup[0].legnum}`,
      moves: currentGroup,
      isGroup: currentGroup.length > 1,
    });
  }

  return groups;
}

// Day-start counter (stat bar). All three counts are over drivers with >= 1
// move for the day, so Started ⊆ On Duty ⊆ Expected. Duty statuses come from
// the Samsara HOS cache; a null status (missing HOS) counts as not on duty.
const ON_DUTY_STATUSES = new Set(['driving', 'onduty', 'yardmove']);
const MOVE_UNDERWAY_STATUSES = new Set(['Started', 'In Transit', 'At Origin', 'At Destination', 'Completed']);

export interface DayStartStats {
  started: number;
  onDuty: number;
  expected: number;
}

export function computeDayStartStats(drivers: DriverWithMoves[]): DayStartStats {
  let started = 0;
  let onDuty = 0;
  let expected = 0;
  for (const driver of drivers) {
    if (!driver.moves || driver.moves.length === 0) continue;
    expected++;
    if (!ON_DUTY_STATUSES.has((driver.current_duty_status ?? '').toLowerCase())) continue;
    onDuty++;
    if (driver.moves.some(m => MOVE_UNDERWAY_STATUSES.has(m.status))) started++;
  }
  return { started, onDuty, expected };
}

// Delivery-appt sort. Appt times are free text from V1 legs (destappttime1/2):
// "14:00", "2:00 PM", "14:00:00", windows like "08:00-12:00", or TBD-ish
// placeholders. Mirrors the backend's parse_origin_appt_time tolerance.
export function parseApptMinutes(timeStr: string | null | undefined): number | null {
  if (!timeStr) return null;
  const start = timeStr.split('-')[0].trim();
  if (!start || ['TBD', 'TBA', 'ASAP', 'EOD'].includes(start.toUpperCase())) return null;
  const m = start.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!m) return null;
  let hour = parseInt(m[1], 10);
  const minute = parseInt(m[2], 10);
  const meridiem = m[3]?.toUpperCase();
  if (meridiem === 'PM' && hour < 12) hour += 12;
  else if (meridiem === 'AM' && hour === 12) hour = 0;
  if (hour > 23 || minute > 59) return null;
  return hour * 60 + minute;
}

export interface NextDeliveryAppt {
  date: string; // YYYY-MM-DD
  minutes: number; // from midnight; 1440 = time TBD (sorts after timed appts that day)
  display: string; // raw appt window, "M/D "-prefixed when date !== today
}

export function nextDeliveryAppt(driver: DriverWithMoves, today: string): NextDeliveryAppt | null {
  let best: NextDeliveryAppt | null = null;
  for (const move of driver.moves ?? []) {
    if (move.status === 'Completed') continue;
    const date = move.destapptdate ?? '';
    if (!date || date === '0000-00-00') continue;
    const parsed = parseApptMinutes(move.appointment_time);
    const minutes = parsed ?? 1440;
    if (best && (date > best.date || (date === best.date && minutes >= best.minutes))) continue;
    const time = parsed !== null ? (move.appointment_time ?? '').trim() : 'TBD';
    const [, mm, dd] = date.split('-');
    const display: string = date === today ? time : `${parseInt(mm, 10)}/${parseInt(dd, 10)} ${time}`;
    best = { date, minutes, display };
  }
  return best;
}


/** Match v1's driver classification: preferences containing OTR are OTR; others are Local. */
export function driverMatchesTruckTypes(
  preference: string | null | undefined,
  selectedTypes: string[],
): boolean {
  if (selectedTypes.length === 0) return true;
  const truckType = preference?.toUpperCase().includes('OTR') ? 'OTR' : 'LOCAL';
  return selectedTypes.includes(truckType);
}

/**
 * "2026-10-15T06:15:02" → "6:15 AM". driver_start_at is already Central
 * wall-clock (the Samsara duty-day rollup converted it), so it is formatted
 * from the string and never passed through Date, which would re-shift it.
 */
export function formatStartTime(value: string | null | undefined): string {
  if (!value) return '';
  const timePart = value.includes('T') ? value.split('T')[1] : value.split(' ')[1] ?? value;
  const [hourStr, minute] = timePart.split(':');
  const hour = parseInt(hourStr, 10);
  if (Number.isNaN(hour) || minute === undefined) return '';
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute} ${suffix}`;
}

/**
 * Sort comparator for the DSM "Start Time" sort option: earliest Samsara
 * on-duty time first. The ISO strings compare lexicographically, which keeps a
 * carried-over shift (prior-day timestamp) ahead of every same-day start.
 * Drivers with no start time fall after everyone with one; the caller keeps
 * them last in both directions.
 */
export function compareByStartTime(a: DriverWithMoves, b: DriverWithMoves): number {
  const aStart = a.driver_start_at || '';
  const bStart = b.driver_start_at || '';
  if (aStart !== bStart) {
    if (!aStart) return 1;
    if (!bStart) return -1;
    return aStart < bStart ? -1 : 1;
  }
  return a.unitnumber.localeCompare(b.unitnumber, undefined, { numeric: true });
}

/**
 * Sort comparator for the DSM "Dispatcher" sort option: group drivers by their
 * driver dispatcher (case-insensitive), then by unit number inside each group.
 * Drivers with no dispatcher assigned fall after every named dispatcher.
 */
export function compareByDispatcher(a: DriverWithMoves, b: DriverWithMoves): number {
  const aDisp = (a.driver_dispatcher || '').trim().toLowerCase();
  const bDisp = (b.driver_dispatcher || '').trim().toLowerCase();
  if (aDisp !== bDisp) {
    if (!aDisp) return 1;
    if (!bDisp) return -1;
    return aDisp.localeCompare(bDisp);
  }
  return a.unitnumber.localeCompare(b.unitnumber, undefined, { numeric: true });
}
